interface IDBService {
    getFile(id: string): Promise<any>;
    addFile(id: string, blob: Blob): Promise<void>;
    updateLastAccessed(file: any): Promise<void>;
    cleanup(targetSize: number): Promise<void>;
    getAllFiles(): Promise<any>;
    deleteFile(id: string): Promise<void>;
    cleanupByTimeSpan(timeSpan: number): Promise<void>;
}

class DBService implements IDBService {
    private db: IDBDatabase | null = null;
    private async getDb(): Promise<IDBDatabase> {
        if (this.db) {
            return this.db;
        }

        return new Promise((resolve, reject) => {
            const request = indexedDB.open("LocalFileStore", 1);

            request.onsuccess = (event: Event) => {
                this.db = (event.target as IDBOpenDBRequest).result;
                resolve(this.db);
            };

            request.onerror = (event: Event) => {
                reject((event.target as IDBRequest).error);
            };

            request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
                const db = (event.target as IDBOpenDBRequest).result;
                db.createObjectStore("cache");
            };
        });
    }

    public async getFile(id: string): Promise<any> {
        try {
            const db = await this.getDb();
            return new Promise(async (resolve, reject) => {
                if (id == null) return reject("Cannot pass null file id");

                const transaction = db.transaction(["cache"], "readonly");
                const fileStore = transaction.objectStore("cache");

                const request = fileStore.get(id);
                request.onsuccess = async (event: Event) => {
                    const result = (event.target as IDBRequest).result;
                    resolve(result);
                };

                request.onerror = (event: Event) => {
                    reject((event.target as IDBRequest).error);
                };
            });
        } catch (error) {
            throw error;
        }
    }

    public async addFile(id: string, blob: Blob): Promise<void> {
        try {
            const db = await this.getDb();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction(["cache"], "readwrite");
                const fileStore = transaction.objectStore("cache");
                const request = fileStore.put({ id, content: blob, lastAccessed: new Date() }, id);

                request.onsuccess = () => resolve();

                request.onerror = (event: Event) => {
                    reject((event.target as IDBRequest).error);
                };
            });
        } catch (error) {
            throw error;
        }
    }

    public async updateLastAccessed(file: any): Promise<void> {
        const db = await this.getDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(["cache"], "readwrite");
            const fileStore = transaction.objectStore("cache");
            if (file) {
                file.lastAccessed = new Date();
                const updateRequest = fileStore.put(file, file.id);
                updateRequest.onsuccess = () => resolve();
                updateRequest.onerror = (event: Event) => {
                    reject((event.target as IDBRequest).error);
                };
            }
        });
    }

    public async cleanup(targetSize: number): Promise<void> {
        try {
            
            // Delete the old legacy "LFS" database if it exists
            // This can be removed after a few months.
            if (window.indexedDB.databases) {
                const dbs = await window.indexedDB.databases();
                if (dbs.some(db => db.name === "LFS")) {
                    window.indexedDB.deleteDatabase("LFS");
                }
            }
            
            const db = await this.getDb();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction(["cache"], "readwrite");
                const fileStore = transaction.objectStore("cache");
                const request = fileStore.getAll();

                request.onsuccess = async (event: Event) => {
                    const files = (event.target as IDBRequest).result;
                    files.sort((a: any, b: any) => new Date(a.lastAccessed).getTime() - new Date(b.lastAccessed).getTime());

                    let totalSize = files.reduce((acc: number, file: any) => acc + file.content.size, 0);

                    while (totalSize > targetSize && files.length > 0) {
                        const fileToDelete = files.shift();
                        totalSize -= fileToDelete.content.size;
                        const deleteRequest = fileStore.delete(fileToDelete.id);

                        await new Promise<void>((resolve, reject) => {
                            deleteRequest.onsuccess = () => resolve();
                            deleteRequest.onerror = (event: Event) => reject((event.target as IDBRequest).error);
                        });
                    }

                    resolve();
                };

                request.onerror = (event: Event) => {
                    reject((event.target as IDBRequest).error);
                };
            });
        } catch (error) {
            throw error;
        }
    }

    public async getAllFiles(): Promise<any[]> {
        try {
            const db = await this.getDb();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction(["cache"], "readonly");
                const fileStore = transaction.objectStore("cache");
                const request = fileStore.getAll();

                request.onsuccess = (event: Event) => {
                    resolve((event.target as IDBRequest).result.map((file: any) => ({
                        id: file.id,
                        // lastAccessed: file.lastAccessed,
                        size: file.content.size
                    })));
                };

                request.onerror = (event: Event) => {
                    reject((event.target as IDBRequest).error);
                };
            });
        } catch (error) {
            throw error;
        }
    }

    public async deleteFile(id: string): Promise<void> {
        try {
            const db = await this.getDb();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction(["cache"], "readwrite");
                const fileStore = transaction.objectStore("cache");
                const request = fileStore.delete(id);

                request.onsuccess = () => resolve();
                request.onerror = (event: Event) => {
                    reject((event.target as IDBRequest).error);
                };
            });
        } catch (error) {
            throw error;
        }
    }

    public async cleanupByTimeSpan(timeSpan: number): Promise<void> {
        try {
            // Delete the old legacy "LFS" database if it exists
            // This can be removed after a few months.
            if (window.indexedDB.databases) {
                const dbs = await window.indexedDB.databases();
                if (dbs.some(db => db.name === "LFS")) {
                    window.indexedDB.deleteDatabase("LFS");
                }
            }
            
            const db = await this.getDb();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction(["cache"], "readwrite");
                const fileStore = transaction.objectStore("cache");
                const request = fileStore.getAll();

                request.onsuccess = async (event: Event) => {
                    const files = (event.target as IDBRequest).result;
                    const cutoffDate = new Date(Date.now() - timeSpan);

                    for (const file of files) {
                        if (new Date(file.lastAccessed) < cutoffDate) {
                            const deleteRequest = fileStore.delete(file.id);
                            await new Promise<void>((resolve, reject) => {
                                deleteRequest.onsuccess = () => resolve();
                                deleteRequest.onerror = (event: Event) => reject((event.target as IDBRequest).error);
                            });
                        }
                    }

                    resolve();
                };

                request.onerror = (event: Event) => {
                    reject((event.target as IDBRequest).error);
                };
            });
        } catch (error) {
            throw error;
        }
    }
}

const dbService: IDBService = new DBService();

async function downloadFile(url: string, progressEventHandler?: (event: ProgressEvent) => void): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        if (progressEventHandler) {
            request.addEventListener('progress', progressEventHandler);
        }

        request.addEventListener('load', () => {
            if (request.status >= 200 && request.status <= 299) {
                resolve(request.response);
            } else {
                reject(new Error(`File returned a ${request.status} response code.`));
            }
        });

        request.addEventListener('error', () => reject(new Error("An error occurred during the transaction.")));
        request.addEventListener('timeout', () => reject(new Error("The request timed out.")));
        request.addEventListener('abort', () => reject(new Error("The request was aborted.")));

        request.responseType = "blob";
        request.open("GET", url);
        request.send();
    });
}

async function addFile(key: string, filePath: string, expectedFileSize: number = -1, dotnetHelper?: any): Promise<boolean> {
    try {
        const progressEventHandler = (event: ProgressEvent) => {
            dotnetHelper?.invokeMethodAsync("HandleOnFileDownloadProgress", key, event.loaded);
        };

        const result = await dbService.getFile(key);
        // Only use cached if it's the same file
        if (result && (expectedFileSize == -1 || expectedFileSize == result.content.size)) {
            if (dotnetHelper) {
                await dotnetHelper.invokeMethodAsync("HandleOnFileDownloadProgress", key, result.content.size);
            }
            await dbService.updateLastAccessed(result);
            return true;
        }

        const response = await downloadFile(filePath, progressEventHandler);
        await dbService.addFile(key, response);
        return true;
    } catch (error) {
        throw error;
    }
}

async function addStream(key: string, mimeType: string, contentStreamReference:any): Promise<boolean> {
    try {
        const arrayBuffer = await contentStreamReference.arrayBuffer();
        const blob = new Blob([arrayBuffer], { type: mimeType });
        await dbService.addFile(key, blob)
        return true;
    } catch (error) {
        throw error;
    }
}

async function addTemporaryFile(filePath: string): Promise<string> {
    try {
        const response = await downloadFile(filePath);
        return URL.createObjectURL(response);
    } catch (error) {
        throw error;
    }
}

async function getFileUrl(key: string): Promise<string | null> {
    try {
        const result = await dbService.getFile(key);
        if (result) {
            return URL.createObjectURL(result.content);
        }
        return null;
    } catch (error) {
        throw error;
    }
}

async function getStorageUsage(): Promise<number> {
    try {
        const data = await navigator.storage.estimate();
        return data.usage || 0;
    } catch (error) {
        throw error;
    }
}

async function getStorageQuota(): Promise<number> {
    try {
        const data = await navigator.storage.estimate();
        return data.quota || 0;
    } catch (error) {
        throw error;
    }
}

async function deleteFile(key: string): Promise<void> {
    await dbService.deleteFile(key);
}

async function createObjectUrl(contentStreamReference: any, mimeType: string): Promise<string> {
    const arrayBuffer = await contentStreamReference.arrayBuffer();
    const blob = new Blob([arrayBuffer], { type: mimeType });
    return URL.createObjectURL(blob);
}

function revokeObjectURL (url: string) : void {
    URL.revokeObjectURL(url);
}

async function fetchObjectUrl(objectUrl: string) : Promise<Uint8Array> {
    const response = await fetch(objectUrl);
    const arrayBuffer = await response.arrayBuffer();
    return new Uint8Array(arrayBuffer);
}

(window as any).localFileStore = {
    addFile,
    addStream,
    getFileUrl,
    getStorageUsage,
    getStorageQuota,
    addTemporaryFile,
    cleanup: (targetSize: number) => dbService.cleanup(targetSize),
    getAllFiles: () => dbService.getAllFiles(),
    deleteFile,
    revokeObjectURL,
    createObjectUrl,
    fetchObjectUrl,
    cleanupByTimeSpan: (timeSpan: number) => dbService.cleanupByTimeSpan(timeSpan),
};
